// =============================================================================
//  Copyright : 2026 semantics GmbH
//  Date      : $Date: 2026-06-19 13:40:04 +0200 (Fri, 19 Jun 2026) $
//  Author    : Olli Powroznik
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 163880 $
//  Purpose   : Hook for measuring and computing responsive toolbar layout
// =============================================================================
// ---Third-party imports
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
// ---Framework imports
import { useDimensions } from 'uix/hooks';
// ---Local imports
import { computeToolbarLayout, sameLayout } from './utils';
const DIM_DELTA = 2;
export const useResponsiveLayout = ({ groups, signature, ribbons })=>{
    let containerRef = useRef();
    let [containerDim, observeContainer] = useDimensions({
        defaults: null,
        delta: DIM_DELTA
    });
    let widths = useRef([]);
    let menuW = useRef([]);
    let groupW = useRef([]);
    let titleW = useRef([]);
    let chrome = useRef([]);
    let pad = useRef([]);
    let collapsedPerGroup = useRef([]);
    let moreW = useRef(0);
    let collapsedW = useRef(0);
    let [phase, setPhase] = useState('measure');
    let [result, setResult] = useState({
        layout: null,
        overflow: false
    });
    let [version, setVersion] = useState(0);
    let bump = useCallback(()=>setVersion((v)=>v + 1), []);
    let setContainerRef = useCallback((el)=>{
        containerRef.current = el;
        observeContainer.current = el;
    }, []);
    let onCell = useCallback((gi, ii, w)=>{
        if (!widths.current[gi]) widths.current[gi] = [];
        if (Math.abs((widths.current[gi][ii] ?? -1) - w) > 0.5) {
            widths.current[gi][ii] = w;
            bump();
        }
    }, [
        bump
    ]);
    let onMenu = useCallback((gi, w)=>{
        if (Math.abs((menuW.current[gi] ?? -1) - w) > 0.5) {
            menuW.current[gi] = w;
            bump();
        }
    }, [
        bump
    ]);
    let onTitle = useCallback((gi, w)=>{
        if (Math.abs((titleW.current[gi] ?? -1) - w) > 0.5) {
            titleW.current[gi] = w;
            bump();
        }
    }, [
        bump
    ]);
    let onCollapsedGroup = useCallback((gi, w)=>{
        if (Math.abs((collapsedPerGroup.current[gi] ?? -1) - w) > 0.5) {
            collapsedPerGroup.current[gi] = w;
            bump();
        }
    }, [
        bump
    ]);
    let onMore = useCallback((w)=>{
        if (moreW.current !== w) {
            moreW.current = w;
            bump();
        }
    }, [
        bump
    ]);
    let onCollapsed = useCallback((w)=>{
        if (collapsedW.current !== w) {
            collapsedW.current = w;
            bump();
        }
    }, [
        bump
    ]);
    let onGroup = useCallback((gi, w)=>{
        if (Math.abs((groupW.current[gi] ?? -1) - w) > 0.5) {
            groupW.current[gi] = w;
            bump();
        }
    }, [
        bump
    ]);
    useLayoutEffect(()=>{
        widths.current = [];
        menuW.current = [];
        groupW.current = [];
        titleW.current = [];
        chrome.current = [];
        pad.current = [];
        collapsedPerGroup.current = [];
        setPhase('measure');
        setResult({
            layout: null,
            overflow: false
        });
    }, [
        signature
    ]);
    useLayoutEffect(()=>{
        if (containerDim?.width == null) return;
        let ready = groups.every((g, gi)=>{
            let cells = (widths.current[gi] || []).slice(0, g.items.length).filter((w)=>w != null);
            return menuW.current[gi] != null && groupW.current[gi] != null && cells.length === g.items.length && (!ribbons || !g.props.title || titleW.current[gi] != null);
        });
        if (!ready) return;
        if (phase === 'measure') {
            groups.forEach((g, gi)=>{
                let sum = (widths.current[gi] || []).slice(0, g.items.length).reduce((a, w)=>a + w, 0);
                chrome.current[gi] = Math.max(0, (menuW.current[gi] || 0) - sum);
                pad.current[gi] = Math.max(0, (groupW.current[gi] || 0) - Math.max(menuW.current[gi] || 0, ribbons ? titleW.current[gi] || 0 : 0));
            });
            setPhase('live');
        }
        let layoutGroups = groups.map((g, gi)=>({
                items: (widths.current[gi] || []).slice(0, g.items.length),
                chrome: chrome.current[gi] || 0,
                title: ribbons ? titleW.current[gi] || 0 : 0,
                collapsedW: collapsedPerGroup.current[gi],
                pad: pad.current[gi] || 0
            }));
        let { layout, overflow } = computeToolbarLayout({
            groups: layoutGroups,
            containerWidth: containerDim.width,
            moreWidth: moreW.current,
            collapsedWidth: collapsedW.current || moreW.current
        });
        setResult((prev)=>prev.overflow === overflow && sameLayout(prev.layout, layout) ? prev : {
                layout,
                overflow
            });
    }, [
        phase,
        version,
        signature,
        ribbons,
        containerDim?.width
    ]);
    let reporters = useMemo(()=>({
            onCell,
            onMenu,
            onTitle,
            onGroup,
            onCollapsedGroup,
            onMore,
            onCollapsed
        }), [
        onCell,
        onMenu,
        onTitle,
        onGroup,
        onCollapsedGroup,
        onMore,
        onCollapsed
    ]);
    let valid = result.layout && result.layout.length === groups.length ? result.layout : null;
    return {
        setContainerRef,
        containerRef,
        measuring: phase === 'measure' || valid == null,
        layout: valid,
        overflow: result.overflow,
        reporters
    };
};
